import { Fragment } from "react";
import { Link } from "react-router";
import { useOrders } from "@/api/useCatalogue";
import { StatusPill } from "@/components/StatusPill";
import { FulfilmentTimeline } from "@/components/FulfilmentTimeline";
import { ArrowRightIcon } from "@/components/icons";
import { cn } from "@/lib/cn";

/**
 * My orders — order history with status. Phase 2 wires a real order
 * resource; Phase 4 adds the contract-proof download link per row.
 */
export function OrdersPage() {
  const { data: orders = [], isLoading } = useOrders();

  return (
    <div className="mx-auto max-w-7xl px-8 py-10">
      <header className="mb-8">
        <div className="mb-2 flex items-center gap-2 text-eyebrow font-semibold uppercase text-fg-mute">
          <span aria-hidden className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: "var(--studio)" }} />
          My orders
        </div>
        <h1 className="m-0 mb-2 text-xl font-semibold leading-tight text-fg-1">
          Order history
        </h1>
        <p className="m-0 max-w-2xl text-base leading-relaxed text-fg-3">
          Recent orders. Click to view proof or reorder.
        </p>
      </header>

      <div className="overflow-hidden rounded-lg border border-[var(--border-1)] bg-white">
        {isLoading ? (
          <div className="p-6 text-fg-3">Loading…</div>
        ) : (
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-[var(--border-1)] bg-bg-2 text-left">
                {["Order", "Units", "Total", "Status", "Placed", ""].map((h) => (
                  <th
                    key={h}
                    className="px-3.5 py-2.5 text-xxs font-semibold uppercase tracking-wider text-fg-3"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => {
                // royalMailTracking isn't carried on the list row today; read
                // defensively so it appears once the hook passes it through.
                const rmTracking = (o as { royalMailTracking?: string | null }).royalMailTracking;
                return (
                  <Fragment key={o.id}>
                    <tr className="border-b-0 hover:bg-bg-1">
                      <td className="px-3.5 pt-3 pb-1 text-sm font-medium text-fg-1">{o.name}</td>
                      <td className="px-3.5 pt-3 pb-1 font-mono text-xs tabular-nums text-fg-3">{o.units}</td>
                      <td className="px-3.5 pt-3 pb-1 text-sm font-semibold tabular-nums text-fg-1">{o.total}</td>
                      <td className="px-3.5 pt-3 pb-1">
                        <StatusPill status={o.status} />
                      </td>
                      <td className="px-3.5 pt-3 pb-1 text-sm tabular-nums text-fg-3">{o.placed}</td>
                      <td className="px-3.5 pt-3 pb-1 text-right">
                        <Link
                          to="/catalogue"
                          className={cn(
                            "inline-flex h-8 items-center justify-center gap-2 rounded-md px-3 text-2xs font-semibold text-fg-2 no-underline transition-colors",
                            "hover:bg-[var(--bg-1)] hover:text-fg-1 active:bg-[var(--bg-2)]",
                            "outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-focus)] focus-visible:ring-offset-1",
                          )}
                        >
                          Open <ArrowRightIcon width={11} height={11} />
                        </Link>
                      </td>
                    </tr>
                    <tr className="border-b border-[var(--border-1)] last:border-0">
                      <td colSpan={6} className="px-3.5 pb-4 pt-1">
                        <FulfilmentTimeline
                          status={o.status}
                          royalMailTracking={rmTracking}
                          className="mx-auto max-w-[560px]"
                        />
                      </td>
                    </tr>
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
